class CollectionSlider {
  constructor(section) {
    this.section = section;
    this.track = section.querySelector('.collection-slider__track');
    this.prevBtn = section.querySelector('.collection-slider__arrow--prev');
    this.nextBtn = section.querySelector('.collection-slider__arrow--next');
    this.slides = section.querySelectorAll('.collection-slider__slide');
    
    this.autoplay = section.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(section.dataset.autoplaySpeed) || 5000;
    this.autoplayTimer = null;
    this.resizeObserver = null;
    
    // Bind methods
    this.handleScroll = this.handleScroll.bind(this);
    this.slidePrev = this.slidePrev.bind(this);
    this.slideNext = this.slideNext.bind(this);
    this.pauseAutoplay = this.pauseAutoplay.bind(this);
    this.resumeAutoplay = this.resumeAutoplay.bind(this);
    this.updateButtonStates = this.updateButtonStates.bind(this);
    
    if (!this.track || this.slides.length === 0) return;
    
    this.init();
  }
  
  init() {
    this.addEventListeners();
    
    // Give layout a moment to calculate, then check button states
    requestAnimationFrame(() => this.updateButtonStates());
    
    // Set up a resize observer to constantly monitor width changes (replaces window.resize)
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => this.updateButtonStates());
      });
      this.resizeObserver.observe(this.track);
    }
    
    if (this.autoplay) this.startAutoplay();
  }
  
  get slideScrollDistance() {
    return this.slides[0].offsetWidth + parseFloat(getComputedStyle(this.track).gap || 0);
  }

  addEventListeners() {
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    
    if (this.prevBtn) this.prevBtn.addEventListener('click', this.slidePrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.slideNext);
    
    // Autoplay pause on hover/touch
    this.section.addEventListener('mouseenter', this.pauseAutoplay);
    this.section.addEventListener('mouseleave', this.resumeAutoplay);
    this.track.addEventListener('touchstart', this.pauseAutoplay, { passive: true });
    this.track.addEventListener('touchend', this.resumeAutoplay, { passive: true });
  }
  
  slidePrev() {
    this.track.scrollBy({ left: -this.slideScrollDistance, behavior: 'smooth' });
    if (this.autoplay) this.startAutoplay(); 
  }
  
  slideNext() {
    this.track.scrollBy({ left: this.slideScrollDistance, behavior: 'smooth' });
    if (this.autoplay) this.startAutoplay(); 
  }
  
  handleScroll() {
    if (this.scrollTimeout) cancelAnimationFrame(this.scrollTimeout);
    this.scrollTimeout = requestAnimationFrame(() => this.updateButtonStates());
  }
  
  updateButtonStates() {
    const scrollLeft = Math.ceil(this.track.scrollLeft);
    const maxScroll = Math.floor(this.track.scrollWidth - this.track.clientWidth);
    
    // If the content fits within the screen, hide/disable both buttons
    const noScrollNeeded = maxScroll <= 0;

    if (this.prevBtn) {
      this.prevBtn.disabled = noScrollNeeded || scrollLeft <= 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = noScrollNeeded || scrollLeft >= maxScroll - 5;
    }
  }
  
  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplay) return;
    
    this.autoplayTimer = setInterval(() => {
      const isAtEnd = Math.ceil(this.track.scrollLeft) >= Math.floor(this.track.scrollWidth - this.track.clientWidth) - 5;
      
      if (isAtEnd) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this.slideNext();
      }
    }, this.autoplaySpeed);
  }
  
  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  pauseAutoplay() { this.stopAutoplay(); }
  resumeAutoplay() { if (this.autoplay) this.startAutoplay(); }

  destroy() {
    this.stopAutoplay();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    
    this.track.removeEventListener('scroll', this.handleScroll);
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.slidePrev);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.slideNext);
    this.section.removeEventListener('mouseenter', this.pauseAutoplay);
    this.section.removeEventListener('mouseleave', this.resumeAutoplay);
  }
}

// Initialize on page load
const initSliders = () => {
  document.querySelectorAll('[data-section-type="collection-slider"]').forEach(section => {
    if (!section.dataset.sliderInitialized) {
      section.sliderInstance = new CollectionSlider(section);
      section.dataset.sliderInitialized = true;
    }
  });
};

document.addEventListener('DOMContentLoaded', initSliders);

// Shopify Theme Editor Integration
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-section-type="collection-slider"]');
    if (section) {
      section.sliderInstance = new CollectionSlider(section);
      section.dataset.sliderInitialized = true;
    }
  });
  
  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector('[data-section-type="collection-slider"]');
    if (section && section.sliderInstance) {
      section.sliderInstance.destroy();
      delete section.dataset.sliderInitialized;
    }
  });
}